import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Radio,
  Slider,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import formatDate from '../../components/formatDate';
import {
  cropPulseCardHero,
  getPulseCard,
  getPulseErrorMessage,
  getPulsePayload,
  getPulseUploadedImageUrl,
  isPulseApiSuccess,
  isPulseOptionsLockedError,
  patchPulseCard,
  regeneratePulseCard,
  uploadPulseImage,
} from '../../services/pulseApi';
import {
  COPY_LIMITS,
  HERO_PRESENTATIONS,
  PULSE_BUCKETS,
  PULSE_STATUSES,
  adminHotTakeOptionError,
  canEditCorrectOption,
  cardToForm,
  clampHeroFocus,
  copyLengthHint,
  datetimeLocalToIso,
  diffCardPayload,
  diffHotTakePayload,
  emptyOption,
  formatNumber,
  formatPulseDate,
  formatRate,
  heroSourceUrl,
  hotTakeToForm,
  isAdminHotTake,
  isFanStoryCard,
  isFutureIso,
  isHotTakeCard,
  isInstinctCard,
  isKnowledgeCard,
  isMatchRatingCard,
  isMatchTiedPredictTrivia,
  isReadMoreCard,
  parseCardPayload,
  resolveCorrectOptionId,
  sourceTypeLabel,
  statusChipColor,
} from './pulseUtils';

function StatCard({ label, value }) {
  return (
    <Paper sx={{ p: 2, textAlign: 'center', height: '100%' }}>
      <Typography variant="h5" fontWeight={700}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Paper>
  );
}

function PulseCardEditor() {
  const { id } = useParams();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [cropping, setCropping] = useState(false);
  const [focusX, setFocusX] = useState(0.5);
  const [focusY, setFocusY] = useState(0.5);
  const [regenerating, setRegenerating] = useState('');
  const [forceReplaceOptions, setForceReplaceOptions] = useState(false);
  const [error, setError] = useState('');
  const [card, setCard] = useState(null);
  const [form, setForm] = useState(cardToForm(null));
  const [hotTakeForm, setHotTakeForm] = useState(hotTakeToForm(null));
  const [correctOptionId, setCorrectOptionId] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const loadCard = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await getPulseCard(id);
      if (!isPulseApiSuccess(result)) {
        setError(getPulseErrorMessage(result, 'Failed to load Pulse card.'));
        setCard(null);
        return;
      }
      const parsed = parseCardPayload(getPulsePayload(result));
      setCard(parsed);
      setForm(cardToForm(parsed));
      setHotTakeForm(hotTakeToForm(parsed?.hot_take));
      setCorrectOptionId(resolveCorrectOptionId(parsed));
    } catch (fetchError) {
      setError(fetchError.message || 'Failed to load Pulse card.');
      setCard(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadCard();
  }, [loadCard]);

  useEffect(() => {
    if (!card) return;
    setFocusX(clampHeroFocus(card.hero_focus_x ?? card.heroFocusX));
    setFocusY(clampHeroFocus(card.hero_focus_y ?? card.heroFocusY));
  }, [card]);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateOption = (index, field, value) => {
    setForm((prev) => {
      const options = [...prev.options];
      options[index] = { ...options[index], [field]: value };
      return { ...prev, options };
    });
  };

  const applyCandidate = (index, candidate) => {
    if (!candidate || card?.options_locked) return;
    setForm((prev) => {
      const options = [...prev.options];
      options[index] = {
        ...options[index],
        id: candidate.id || '',
        label: candidate.label || '',
      };
      return { ...prev, options };
    });
  };

  const addOption = () => {
    if (card?.options_locked) return;
    setForm((prev) => {
      if (isAdminHotTake(card) && prev.options.length >= 4) return prev;
      const extras =
        isAdminHotTake(card) && prev.options.length === 2
          ? [emptyOption(), emptyOption()]
          : [emptyOption()];
      return { ...prev, options: [...prev.options, ...extras] };
    });
  };

  const removeOption = (index) => {
    if (card?.options_locked) return;
    const removedId = form.options[index]?.id;
    setForm((prev) => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index),
    }));
    if (removedId && correctOptionId === removedId) setCorrectOptionId('');
  };

  const updateHotTakeOption = (index, field, value) => {
    setHotTakeForm((prev) => {
      const options = [...prev.options];
      options[index] = { ...options[index], [field]: value };
      return { ...prev, options };
    });
  };

  const removeHotTakeOption = (index) => {
    setHotTakeForm((prev) => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index),
    }));
  };

  const handleUploadImage = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const result = await uploadPulseImage(file);
      if (!isPulseApiSuccess(result)) {
        setError(getPulseErrorMessage(result, 'Failed to upload image.'));
        return;
      }
      const url = getPulseUploadedImageUrl(result);
      if (!url) {
        setError('Upload finished but image URL was missing.');
        return;
      }
      updateField('image_url', url);
      setSnackbar({ open: true, message: 'Image uploaded. Save the card to persist image_url.' });
    } catch (uploadError) {
      setError(uploadError.message || 'Failed to upload image.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleHeroCrop = async () => {
    if (!isFanStoryCard(card)) return;
    setCropping(true);
    setError('');
    try {
      const result = await cropPulseCardHero(id, {
        focusX: Number(focusX),
        focusY: Number(focusY),
      });
      if (!isPulseApiSuccess(result)) {
        setError(getPulseErrorMessage(result, 'Failed to recrop the Fan Story hero.'));
        return;
      }
      const parsed = parseCardPayload(getPulsePayload(result));
      applyCardFromServer(
        parsed,
        'Hero recropped to 4:3. The app deck picks up the new image on the next cards fetch.'
      );
    } catch (cropError) {
      setError(cropError.message || 'Failed to recrop the Fan Story hero.');
    } finally {
      setCropping(false);
    }
  };

  const applyCardFromServer = (parsed, fallbackMessage) => {
    if (!parsed) return;
    setCard((prev) => {
      const nextCard = { ...(prev || {}), ...parsed };
      setForm(cardToForm(nextCard));
      setHotTakeForm(hotTakeToForm(nextCard.hot_take));
      setCorrectOptionId(resolveCorrectOptionId(nextCard));
      return nextCard;
    });
    if (fallbackMessage) setSnackbar({ open: true, message: fallbackMessage });
  };

  const handleRegenerate = async (fields) => {
    if (!isReadMoreCard(card)) return;
    const key = fields ? fields.join(',') : 'all';
    const regeneratingQuestion =
      !fields || fields.includes('question') || fields.includes('hot_take');
    setRegenerating(key);
    setError('');
    try {
      const body = fields ? { fields } : {};
      if (
        regeneratingQuestion &&
        card?.hot_take?.options_locked &&
        forceReplaceOptions
      ) {
        body.force_replace_options = true;
      }
      const result = await regeneratePulseCard(id, body);
      if (!isPulseApiSuccess(result)) {
        setError(getPulseErrorMessage(result, 'Failed to regenerate AI fields.'));
        return;
      }
      const payload = getPulsePayload(result) || {};
      const parsed = parseCardPayload(payload);
      const regenerated = payload.regenerated || {};
      const questionMeta = payload.question_regenerate;

      // Apply regenerated fields into form immediately (also merge full card)
      setForm((prev) => {
        const next = { ...prev };
        if (regenerated.title != null) next.title = regenerated.title;
        if (regenerated.summary != null) next.summary = regenerated.summary;
        return next;
      });
      if (regenerated.question != null || regenerated.hot_take_options) {
        setHotTakeForm((prev) => ({
          ...prev,
          ...(regenerated.question != null ? { statement: regenerated.question } : {}),
          ...(Array.isArray(regenerated.hot_take_options)
            ? {
                options: regenerated.hot_take_options.map((o) => ({
                  id: o.id || '',
                  label: o.label || '',
                })),
              }
            : {}),
        }));
      }

      const parts = [];
      if (regenerated.title) parts.push('title');
      if (regenerated.summary) parts.push('summary');
      if (regenerated.question) parts.push('question');
      if (regenerated.hot_take_options) parts.push('hot-take options');
      let message = parts.length ? `Regenerated: ${parts.join(', ')}` : 'AI fields regenerated.';
      if (questionMeta?.warning) {
        message = `${message} — ${questionMeta.warning}`;
      } else if (questionMeta?.options_replaced) {
        message = `${message} (option ids replaced)`;
      }

      applyCardFromServer(parsed, message);
      if (parsed) {
        // Re-apply regenerated onto form after server merge in case GET shape differs
        if (regenerated.title != null || regenerated.summary != null) {
          setForm((prev) => ({
            ...prev,
            ...(regenerated.title != null ? { title: regenerated.title } : {}),
            ...(regenerated.summary != null ? { summary: regenerated.summary } : {}),
          }));
        }
        if (regenerated.question != null) {
          setHotTakeForm((prev) => ({ ...prev, statement: regenerated.question }));
        }
      }
      setForceReplaceOptions(false);
    } catch (regenError) {
      setError(regenError.message || 'Failed to regenerate AI fields.');
    } finally {
      setRegenerating('');
    }
  };

  const validateStatusChange = () => {
    if (form.status === 'active' && card?.status === 'expired') {
      const deadline = datetimeLocalToIso(form.deadline_at);
      const expires = datetimeLocalToIso(form.expires_at);
      if (!isFutureIso(deadline) && !isFutureIso(expires)) {
        return 'Expired → active needs a future deadline_at or expires_at.';
      }
    }
    return '';
  };

  const handleSave = async (statusOverride) => {
    const nextForm = statusOverride ? { ...form, status: statusOverride } : form;

    if (nextForm.status === 'active' && card?.status === 'expired') {
      const deadline = datetimeLocalToIso(nextForm.deadline_at);
      const expires = datetimeLocalToIso(nextForm.expires_at);
      if (!isFutureIso(deadline) && !isFutureIso(expires)) {
        setError(
          'Expired cards can return to active only with a future deadline_at or expires_at. Otherwise create a new card.'
        );
        return;
      }
    }

    if (isAdminHotTake(card)) {
      const optionError = adminHotTakeOptionError(nextForm.options);
      if (optionError) {
        setError(optionError);
        return;
      }
    }

    const payload = diffCardPayload(card, nextForm);
    if (payload.__options_lock_error) {
      setError(payload.__options_lock_error);
      return;
    }

    if (isHotTakeCard(card)) {
      const statement = String(nextForm.title || '').trim();
      if (statement && statement !== String(card.statement || '').trim()) {
        payload.statement = statement;
      }
    }

    const showCorrect = canEditCorrectOption(card);
    if (showCorrect) {
      const prevCorrect = resolveCorrectOptionId(card);
      const nextCorrect = String(correctOptionId || '').trim();
      if (nextCorrect !== prevCorrect) {
        payload.correct_option_id = nextCorrect || null;
      }
      // Trivia: when options change, always send correct_option_id with them
      if (payload.options && nextCorrect) {
        payload.correct_option_id = nextCorrect;
      }
    }

    if (isReadMoreCard(card) && (card.hot_take || hotTakeForm.statement.trim())) {
      const htDiff = diffHotTakePayload(card.hot_take, hotTakeForm, {
        optionsLocked: !!card.hot_take?.options_locked,
      });
      if (htDiff?.__error) {
        setError(htDiff.__error);
        return;
      }
      if (htDiff) payload.hot_take = htDiff;
    }

    if (Object.keys(payload).length === 0) {
      setSnackbar({ open: true, message: 'No changes to save.' });
      return;
    }

    if (statusOverride) setForm(nextForm);

    setSaving(true);
    setError('');
    try {
      const result = await patchPulseCard(id, payload);
      if (!isPulseApiSuccess(result)) {
        if (isPulseOptionsLockedError(result)) {
          setError(
            getPulseErrorMessage(
              result,
              'Options locked: labels and trivia correct answer are allowed; adding/removing/changing option ids is not.'
            )
          );
        } else {
          setError(getPulseErrorMessage(result, 'Failed to save Pulse card.'));
        }
        return;
      }
      const parsed = parseCardPayload(getPulsePayload(result));
      const nextCard =
        parsed && (parsed.id || parsed.status)
          ? { ...card, ...parsed }
          : { ...card, ...payload, status: nextForm.status };
      setCard(nextCard);
      setForm(cardToForm(nextCard));
      setHotTakeForm(hotTakeToForm(nextCard.hot_take));
      setCorrectOptionId(resolveCorrectOptionId(nextCard) || correctOptionId);
      setSnackbar({ open: true, message: 'Card saved.' });
    } catch (saveError) {
      setError(saveError.message || 'Failed to save Pulse card.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!card) {
    return (
      <Box>
        <Button component={Link} to="/admin/pulse" startIcon={<ArrowBackIcon />}>
          Back to cards
        </Button>
        <Alert severity="error" sx={{ mt: 2 }}>
          {error || 'Pulse card not found.'}
        </Alert>
      </Box>
    );
  }

  const adminHotTake = isAdminHotTake(card);
  const locked = !!card.options_locked;
  const readMore = isReadMoreCard(card);
  const fanStory = isFanStoryCard(card);
  const cropSource = heroSourceUrl(card);
  const hotTakeDirect = isHotTakeCard(card);
  const matchTied = isMatchTiedPredictTrivia(card);
  const candidates = matchTied ? card.option_candidates || [] : [];
  const hasCandidates = candidates.length > 0;
  const showCorrect = canEditCorrectOption(card);
  const instinct = isInstinctCard(card) || (card.has_correct_answer === false && !showCorrect);
  const knowledge = isKnowledgeCard(card) || showCorrect;
  const stats = card.stats || {};
  const statusOptions = PULSE_STATUSES.filter((s) => s.value);
  const linkedHotTake = card.hot_take;
  const htLocked = !!linkedHotTake?.options_locked;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton component={Link} to="/admin/pulse">
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ ml: 1 }}>
            <Typography variant="h6">{card.title || 'Pulse card'}</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <Chip size="small" label={card.status} color={statusChipColor(card.status)} />
              <Chip size="small" variant="outlined" label={card.category || card.card_type || '—'} />
              {isMatchRatingCard(card) && (
                <Chip size="small" color="secondary" label="Rate Player" />
              )}
              {card.source_type && (
                <Chip size="small" variant="outlined" label={sourceTypeLabel(card.source_type)} />
              )}
              {locked && <Chip size="small" color="warning" label="options locked" />}
            </Box>
          </Box>
        </Box>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        Cards are never deleted. <strong>bleed</strong> = photo band on top; <strong>backdrop</strong> =
        taller photo behind the question (joystick cards force backdrop on serve). Trivia / predictions
        show a hero <em>only</em> when <code>image_url</code> is set. Upload to S3 then save, or paste
        HTTPS URL. Changing entry fee restamps <code>cost.fan_energy</code> on api_post CTAs.
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {card.failed_reason && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Failed reason: {card.failed_reason}
        </Alert>
      )}

      {card.parent_card_id && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Hot Take parent:{' '}
          <Link to={`/admin/pulse/cards/${encodeURIComponent(card.parent_card_id)}`}>
            {card.parent_card_id}
          </Link>
          . Unpublishing or expiring the parent hides this Hot Take on the app deck.
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <StatCard label="Submissions" value={formatNumber(stats.submissions)} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="Pending" value={formatNumber(stats.pending)} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="Completed" value={formatNumber(stats.completed)} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="Awarded (people)" value={formatNumber(stats.awarded)} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="Unique people" value={formatNumber(stats.unique_submissions)} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="FE spent" value={formatNumber(stats.fe_spent)} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="Views" value={formatNumber(stats.views)} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="Skips" value={formatNumber(stats.skips)} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="Interaction rate" value={formatRate(stats.interaction_rate)} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="Skip rate" value={formatRate(stats.skip_rate)} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="Read more" value={formatNumber(stats.read_more)} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="Expired on serve" value={formatNumber(stats.expired_on_serve)} />
        </Grid>
      </Grid>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2, mt: -1 }}>
        Views are submissions + skips. Interaction % and skip % are shares of that total and add to
        100. Both read 0 when nobody has submitted or skipped.
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Card details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                  fullWidth
                  label={hotTakeDirect ? 'Hot take question' : 'Title'}
                  value={form.title}
                  onChange={(e) =>
                    updateField(
                      'title',
                      (readMore || hotTakeDirect)
                        ? e.target.value.slice(0, COPY_LIMITS.title.max)
                        : e.target.value
                    )
                  }
                  margin="dense"
                  helperText={
                    hotTakeDirect
                      ? adminHotTake
                        ? `${form.title.length}/${COPY_LIMITS.title.max} — question fans vote on. No correct answer; any vote pays the reward.`
                        : `${copyLengthHint('question', form.title)} — punchy question fans react to`
                      : readMore
                        ? copyLengthHint('title', form.title)
                        : undefined
                  }
                />
                {readMore && (
                  <Button
                    size="small"
                    variant="outlined"
                    sx={{ mt: 1.5, flexShrink: 0 }}
                    disabled={!!regenerating || saving}
                    onClick={() => handleRegenerate(['title'])}
                  >
                    {regenerating === 'title' ? '…' : 'Regenerate'}
                  </Button>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                select
                label="Status"
                value={form.status}
                onChange={(e) => updateField('status', e.target.value)}
                margin="dense"
                helperText={validateStatusChange() || ' '}
              >
                {statusOptions.map((s) => (
                  <MenuItem key={s.value} value={s.value}>
                    {s.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                  fullWidth
                  label="Summary"
                  value={form.summary}
                  onChange={(e) =>
                    updateField(
                      'summary',
                      readMore
                        ? e.target.value.slice(0, COPY_LIMITS.summary.max)
                        : e.target.value
                    )
                  }
                  margin="dense"
                  multiline
                  minRows={2}
                  helperText={readMore ? copyLengthHint('summary', form.summary) : undefined}
                />
                {readMore && (
                  <Button
                    size="small"
                    variant="outlined"
                    sx={{ mt: 1.5, flexShrink: 0 }}
                    disabled={!!regenerating || saving}
                    onClick={() => handleRegenerate(['summary'])}
                  >
                    {regenerating === 'summary' ? '…' : 'Regenerate'}
                  </Button>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Image URL"
                value={form.image_url}
                onChange={(e) => updateField('image_url', e.target.value)}
                margin="dense"
                helperText="HTTPS CDN URL (Cloudflare when enabled), or upload below. Empty → no photo for trivia/predictions on app"
              />
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mt: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  component="label"
                  startIcon={<CloudUploadIcon />}
                  disabled={uploading}
                >
                  {uploading ? 'Uploading…' : 'Upload'}
                  <input
                    ref={fileInputRef}
                    hidden
                    accept="image/*"
                    type="file"
                    onChange={handleUploadImage}
                  />
                </Button>
                <Typography variant="caption" color="text.secondary">
                  POST /admin/pulse/upload-image → CDN image_url → then save card
                </Typography>
              </Box>
              {form.image_url ? (
                <Box
                  component="img"
                  src={form.image_url}
                  alt=""
                  sx={{ mt: 1, maxHeight: 120, maxWidth: 240, objectFit: 'cover', borderRadius: 1 }}
                />
              ) : null}
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                select
                label="Bucket"
                value={form.bucket}
                onChange={(e) => updateField('bucket', e.target.value)}
                margin="dense"
              >
                {PULSE_BUCKETS.map((b) => (
                  <MenuItem key={b.value} value={b.value}>
                    {b.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Hero presentation"
                value={form.hero_presentation}
                onChange={(e) => updateField('hero_presentation', e.target.value)}
                margin="dense"
                helperText="How the photo sits — not whether an image exists. Backdrop: surface wash + opacity 0.82"
              >
                {HERO_PRESENTATIONS.map((p) => (
                  <MenuItem key={p.value || 'default'} value={p.value}>
                    {p.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Hero aspect ratio"
                value={form.hero_aspect_ratio}
                onChange={(e) => updateField('hero_aspect_ratio', e.target.value)}
                margin="dense"
                placeholder="4:3"
                helperText='W:H e.g. "4:3". Empty clears'
              />
            </Grid>
            {fanStory && (
              <Grid item xs={12}>
                <Box sx={{ mt: 1, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Fan Story hero crop (4:3)
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                    Recrops the original image around a focus point so a portrait post is not stuck
                    on a hard centre crop. 0 is left/top, 1 is right/bottom. The app deck reads the
                    new hero on the next cards fetch.
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    Source:{' '}
                    {cropSource ? (
                      <Box component="span" sx={{ wordBreak: 'break-all' }}>
                        {cropSource}
                      </Box>
                    ) : (
                      'no image yet — upload or paste an Image URL and save first'
                    )}
                    {card.hero_focus_x == null && card.hero_focus_y == null
                      ? ' · focus not saved yet (sliders start at centre)'
                      : ` · saved focus ${clampHeroFocus(card.hero_focus_x).toFixed(2)}, ${clampHeroFocus(card.hero_focus_y).toFixed(2)}`}
                  </Typography>
                  {cropSource ? (
                    <Box
                      sx={{
                        width: 280,
                        aspectRatio: '4 / 3',
                        borderRadius: 1,
                        overflow: 'hidden',
                        bgcolor: '#1a1a2e',
                        mb: 1.5,
                      }}
                    >
                      <Box
                        component="img"
                        src={cropSource}
                        alt=""
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          objectPosition: `${focusX * 100}% ${focusY * 100}%`,
                          display: 'block',
                        }}
                      />
                    </Box>
                  ) : null}
                  <Typography variant="caption" color="text.secondary" display="block">
                    Focus X (left → right) {Number(focusX).toFixed(2)}
                  </Typography>
                  <Slider
                    value={focusX}
                    min={0}
                    max={1}
                    step={0.01}
                    onChange={(_, value) => setFocusX(value)}
                    disabled={cropping || saving || !cropSource}
                    valueLabelDisplay="auto"
                    sx={{ maxWidth: 360 }}
                  />
                  <Typography variant="caption" color="text.secondary" display="block">
                    Focus Y (top → bottom) {Number(focusY).toFixed(2)}
                  </Typography>
                  <Slider
                    value={focusY}
                    min={0}
                    max={1}
                    step={0.01}
                    onChange={(_, value) => setFocusY(value)}
                    disabled={cropping || saving || !cropSource}
                    valueLabelDisplay="auto"
                    sx={{ maxWidth: 360, mb: 1 }}
                  />
                  <Button
                    size="small"
                    variant="contained"
                    disabled={cropping || saving || !cropSource}
                    onClick={handleHeroCrop}
                  >
                    {cropping ? 'Cropping…' : 'Apply 4:3 crop'}
                  </Button>
                </Box>
              </Grid>
            )}
            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Reward FE"
                value={form.reward_fe}
                onChange={(e) => updateField('reward_fe', e.target.value)}
                margin="dense"
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Entry fee FE"
                value={form.entry_fee_fe}
                onChange={(e) => updateField('entry_fee_fe', e.target.value)}
                margin="dense"
                helperText="Restamps cost.fan_energy on api_post CTAs"
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Priority"
                value={form.priority}
                onChange={(e) => updateField('priority', e.target.value)}
                margin="dense"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="datetime-local"
                label="Starts at"
                value={form.starts_at}
                onChange={(e) => updateField('starts_at', e.target.value)}
                margin="dense"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="datetime-local"
                label="Deadline at"
                value={form.deadline_at}
                onChange={(e) => updateField('deadline_at', e.target.value)}
                margin="dense"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="datetime-local"
                label="Expires at"
                value={form.expires_at}
                onChange={(e) => updateField('expires_at', e.target.value)}
                margin="dense"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Match #{card.match_id ?? '—'} · {card.source_type || '—'}
            {card.source_id ? ` / ${card.source_id}` : ''}
            {' · Created '}
            {formatPulseDate(card.created_at || card.createdAt)}
            {' · Expires '}
            {formatPulseDate(card.expires_at ?? card.expiresAt)}
            {card.updated_at || card.updatedAt
              ? ` · Updated ${formatDate(card.updated_at || card.updatedAt)}`
              : ''}
          </Typography>
        </CardContent>
      </Card>

      {readMore && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Regenerate all AI fields
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Regenerates title, summary, and hot-take question together. Use the Regenerate buttons
              next to each field for a single field. When regenerating question on a locked hot take,
              option ids stay by default unless you confirm replace below.
            </Typography>
            {htLocked && (
              <FormControlLabel
                sx={{ mb: 1, display: 'block' }}
                control={
                  <Checkbox
                    checked={forceReplaceOptions}
                    onChange={(e) => setForceReplaceOptions(e.target.checked)}
                    size="small"
                  />
                }
                label="Users already voted — still replace options? (force_replace_options)"
              />
            )}
            <Button
              size="small"
              variant="outlined"
              disabled={!!regenerating || saving}
              onClick={() => handleRegenerate()}
            >
              {regenerating === 'all' ? 'Regenerating…' : 'Regenerate all (title + summary + question)'}
            </Button>
          </CardContent>
        </Card>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Options
            </Typography>
            <Button
              startIcon={<AddIcon />}
              onClick={addOption}
              size="small"
              disabled={locked || readMore || (adminHotTake && form.options.length >= 4)}
            >
              {adminHotTake && form.options.length === 2 ? 'Add two options' : 'Add option'}
            </Button>
          </Box>
          {readMore && (
            <Alert severity="info" sx={{ mb: 2 }}>
              News / Fan Story Read More cards have no vote options here — edit the Hot take question
              below.
            </Alert>
          )}
          {adminHotTake && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Admin hot take — no correct answer. Any vote pays the reward immediately. Keep 2 or 4
              options; labels {COPY_LIMITS.optionLabel.soft} characters.
            </Alert>
          )}
          {instinct && !readMore && !adminHotTake && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Prediction (instinct) — no correct answer here; answers settle after the match.
              {hasCandidates
                ? ' Pick players/teams from the dropdown.'
                : matchTied
                  ? ' No option_candidates returned yet — free-form id/label allowed.'
                  : ''}
            </Alert>
          )}
          {knowledge && !readMore && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Trivia (knowledge) — mark exactly one Correct option (stored as{' '}
              <code>correct_option_id</code>, never sent on the app deck).
              {hasCandidates ? ' Match-tied rows: label dropdown from option_candidates.' : ''}
            </Alert>
          )}
          {locked && !readMore && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Options locked after first submission. Allowed: edit labels
              {showCorrect ? ' and change Correct radio' : ''}. Blocked (409): add / remove / change
              option ids.
            </Alert>
          )}
          {!readMore && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>{hasCandidates ? 'Label (candidate)' : 'Label'}</TableCell>
                  {showCorrect && <TableCell width={72}>Correct</TableCell>}
                  <TableCell width={48} />
                </TableRow>
              </TableHead>
              <TableBody>
                {form.options.map((opt, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        value={opt.id}
                        onChange={(e) => updateOption(index, 'id', e.target.value)}
                        disabled={locked}
                        placeholder="player-101"
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: 220 }}>
                      {hasCandidates && !locked ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Autocomplete
                            size="small"
                            options={candidates}
                            getOptionLabel={(c) =>
                              c ? `${c.label}${c.kind ? ` (${c.kind})` : ''}` : ''
                            }
                            isOptionEqualToValue={(a, b) => a?.id === b?.id}
                            value={candidates.find((c) => c.id === opt.id) || null}
                            onChange={(_, value) => applyCandidate(index, value)}
                            renderInput={(params) => (
                              <TextField {...params} placeholder="Pick player/team" />
                            )}
                          />
                          <TextField
                            size="small"
                            fullWidth
                            value={opt.label}
                            onChange={(e) => updateOption(index, 'label', e.target.value)}
                            placeholder="Label (free-form ok)"
                          />
                        </Box>
                      ) : (
                        <TextField
                          size="small"
                          fullWidth
                          value={opt.label}
                          onChange={(e) =>
                            updateOption(
                              index,
                              'label',
                              adminHotTake
                                ? e.target.value.slice(0, COPY_LIMITS.optionLabel.max)
                                : e.target.value
                            )
                          }
                          helperText={adminHotTake ? copyLengthHint('optionLabel', opt.label) : undefined}
                        />
                      )}
                    </TableCell>
                    {showCorrect && (
                      <TableCell>
                        <Radio
                          checked={correctOptionId === opt.id && !!opt.id}
                          onChange={() => setCorrectOptionId(opt.id)}
                          disabled={!opt.id.trim()}
                        />
                      </TableCell>
                    )}
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => removeOption(index)}
                        disabled={locked || form.options.length <= 1 || (adminHotTake && form.options.length <= 2)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {readMore && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Linked Hot Take
            </Typography>
            {!linkedHotTake && !hotTakeForm.statement ? (
              <Alert severity="info">
                No hot take linked (<code>hot_take: null</code>). You can still draft a question below;
                save sends <code>hot_take: {'{ statement }'}</code> (Agree/Disagree kept by default).
              </Alert>
            ) : null}
            {linkedHotTake?.id && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                Hot take id:{' '}
                <Link to={`/admin/pulse/cards/${encodeURIComponent(linkedHotTake.id)}`}>
                  {linkedHotTake.id}
                </Link>{' '}
                · {linkedHotTake.status || '—'}
                {htLocked ? ' · options locked (statement still editable)' : ''}
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <TextField
                fullWidth
                label="Hot take question"
                value={hotTakeForm.statement}
                onChange={(e) =>
                  setHotTakeForm((prev) => ({
                    ...prev,
                    statement: e.target.value.slice(0, COPY_LIMITS.question.max),
                  }))
                }
                margin="dense"
                multiline
                minRows={2}
                helperText={`${copyLengthHint('question', hotTakeForm.statement)} — grammar fixes only need statement; options stay Agree / Disagree`}
              />
              <Button
                size="small"
                variant="outlined"
                sx={{ mt: 1.5, flexShrink: 0 }}
                disabled={!!regenerating || saving}
                onClick={() => handleRegenerate(['question'])}
              >
                {regenerating === 'question' ? '…' : 'Regenerate'}
              </Button>
            </Box>
            {htLocked && (
              <FormControlLabel
                sx={{ mt: 0.5, mb: 1, display: 'block' }}
                control={
                  <Checkbox
                    checked={forceReplaceOptions}
                    onChange={(e) => setForceReplaceOptions(e.target.checked)}
                    size="small"
                  />
                }
                label="Users already voted — still replace options? (sends force_replace_options)"
              />
            )}
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
              Options (labels {COPY_LIMITS.optionLabel.soft} chars — keep Agree / Disagree unless
              Product asks otherwise)
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Label</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {hotTakeForm.options.map((opt, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        value={opt.id}
                        onChange={(e) => updateHotTakeOption(index, 'id', e.target.value)}
                        disabled={htLocked}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        value={opt.label}
                        onChange={(e) =>
                          updateHotTakeOption(
                            index,
                            'label',
                            e.target.value.slice(0, COPY_LIMITS.optionLabel.max)
                          )
                        }
                        helperText={copyLengthHint('optionLabel', opt.label)}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => removeHotTakeOption(index)}
                        disabled={htLocked || hotTakeForm.options.length <= 2}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {htLocked && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Options locked after votes — statement and labels only (same ids). Add/remove/change
                ids blocked unless regenerate with force_replace_options.
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 4 }}>
        <Button variant="contained" onClick={() => handleSave()} disabled={saving}>
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
        <Button
          variant="outlined"
          color="success"
          disabled={saving || form.status === 'active'}
          onClick={() => handleSave('active')}
        >
          Publish
        </Button>
        <Button
          variant="outlined"
          color="warning"
          disabled={saving || form.status === 'disabled'}
          onClick={() => handleSave('disabled')}
        >
          Disable
        </Button>
        <Button
          variant="outlined"
          disabled={saving || form.status === 'expired'}
          onClick={() => handleSave('expired')}
        >
          Expire
        </Button>
        <Button component={Link} to="/admin/pulse">
          Cancel
        </Button>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        message={snackbar.message}
      />
    </Box>
  );
}

export default PulseCardEditor;
